import React, { useState, useEffect, Fragment } from 'react';
import { createTheme, ThemeProvider, Button, makeStyles, Box } from '@material-ui/core';
import { Form, Col, Row, Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import SvgIcon from "@material-ui/core/SvgIcon";
import * as Yup from "yup";
import IconButton from "@material-ui/core/IconButton";

import { MultiSelectInput, SelectInput, TextInput } from '../Helpers/Inputs';
import { HeaderStepper } from '../Quoters/Common/HeaderStepper/HeaderStepper';
import { getSessionStorageItem, setSessionStorageItem, SESSION_STORAGE_POLICY_REVIEW } from '../../sessionStorage';
import ExistingInsuranceIcon from '../../icons/existingInsurance.svg';
import BackNext from '../Quoters/Common/SubComponents/BackNext';
import { Delete } from '@material-ui/icons';
import InputTheme from '../Quoters/Common/Helpers/InputTheme';
import NumberFormatDollars from '../Helpers/Formatters/NumberFormatDollars';

import './ExistingInsurance.scss';


export default function ExistingInsurance(props) {

  const { register, errors, handleSubmit, triggerValidation, setValue } = useForm({
    validationSchema: ExistingInsurance.ValidationSchema(),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const prefill = props.location && props.location.search && props.location.search.toLowerCase().includes('prefill');
  const [formState, setFormState] = useState(ExistingInsurance.DefaultFormState(prefill, getSessionStorageItem(SESSION_STORAGE_POLICY_REVIEW)));

  const handleValueChange = (field, value) => {
    setValue(field, value);
    setFormState({ ...formState, [field]: value });
  };

  const handleCurrentInsuranceTypesChange = (field, value) => {
    handleValueChange(field, value);
    triggerValidation(field);
  } 

  const handleTermPolicyChange = (index, field, value) => {
    let newPoliciesFormState = formState.termPolicies;
    newPoliciesFormState[index][field] = value;
    setFormState({ ...formState, termPolicies: newPoliciesFormState });
  };
  const handlePermPolicyChange = (index, field, value) => {
    let newPoliciesFormState = formState.permPolicies;
    newPoliciesFormState[index][field] = value;
    setFormState({ ...formState, permPolicies: newPoliciesFormState });
  };

  const onSubmit = () => {
    setSessionStorageItem(SESSION_STORAGE_POLICY_REVIEW, formState);
    props.history.push('/policy-review/schedule');
  }

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'currentInsuranceTypes' });
    setValue('currentInsuranceTypes', formState.currentInsuranceTypes);
  }, [register]);

  useEffect(() => {
    if (formState.currentInsuranceTypes.includes('Term Life Insurance') && formState.termPolicies.length === 0) {
      onAddTermPolicy();
    }

    if (formState.currentInsuranceTypes.includes('Permanent Life Insurance') && formState.permPolicies.length === 0) {
      onAddPermPolicy();
    }
    
    if(!formState.currentInsuranceTypes.includes('Term Life Insurance') && formState.termPolicies.length > 0){
      setFormState({...formState, termPolicies: [] });
    }

    if(!formState.currentInsuranceTypes.includes('Permanent Life Insurance') && formState.permPolicies.length > 0){
      setFormState({...formState, permPolicies: [] });
    }
  }, [formState.currentInsuranceTypes]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const onAddTermPolicy = () => {
    setFormState({ ...formState, termPolicies: [...formState.termPolicies, { coverageAmount: '', termLength: '' }] })
  }
  const onRemoveTermPolicy = (index) => {
    setFormState({ ...formState, termPolicies: [...formState.termPolicies.slice(0, index), ...formState.termPolicies.slice(index + 1)] })
  }

  const onAddPermPolicy = () => {
    setFormState({ ...formState, permPolicies: [...formState.permPolicies, { coverageAmount: '', coverageType: '' }] })
  }
  const onRemovePermPolicy = (index) => {
    setFormState({ ...formState, permPolicies: [...formState.permPolicies.slice(0, index), ...formState.permPolicies.slice(index + 1)] })
  }

  const termLengths = [
    { label: '10 Years', value: '10 Years' },
    { label: '15 Years', value: '15 Years' },
    { label: '20 Years', value: '20 Years' },
    { label: '25 Years', value: '25 Years' },
    { label: '30 Years', value: '30 Years' },
    { label: '35 Years', value: '35 Years' },
    { label: '40 Years', value: '40 Years' },
    { label: 'Other', value: 'Other' },
  ];

  const coverageTypes = [
    { label: 'Term Life', value: 'Term Life' },
    { label: 'Group', value: 'Group' },
    { label: 'Whole Life', value: 'Whole Life' },
    { label: 'Single Premium Whole Life', value: 'Single Premium Whole Life' },
    { label: 'Universal Life', value: 'Universal Life' },
    { label: 'Guaranteed Universal Life', value: 'Guaranteed Universal Life' },
    { label: 'Indexed Universal Life', value: 'Indexed Universal Life' },
    { label: 'Variable Universal Life', value: 'Variable Universal Life' },
    { label: 'Supplemental Health', value: 'Supplemental Health' },
    { label: 'Disability Income', value: 'Disability Income' },
    { label: 'Other', value: 'Other' },
  ];

  const useStyles = makeStyles(theme => ({
    stepContainer: {
      backgroundColor: '#f9f9f9',
      border: '1px solid #e2e2e2',
      borderRadius: '0 0 4px 4px',
      paddingTop: '48px',
      marginTop: '-48px',
    },
    formSectionTitle: {
      color: '#333333',
      fontWeight: '700',
      fontSize: '14px',
    },
    addButton: {
      color: '#1C3D6C',
      fontWeight: '700',
      fontSize: '14px',
    },
    lightBackground: {
      backgroundColor: `${theme.palette.primary.main}16`,
    },
  }));
  const classes = useStyles();

  const termForms = formState.termPolicies.map((policy, key) => {
    return <Fragment key={`termForm${key}`}>
      <Col className="title-control d-flex p-0 align-items-center">
        <span className={classes.formSectionTitle}>Term Life Insurance {key > 0 && <Fragment>{key + 1}</Fragment>}  (Optional)</span>
        {key > 0 && <Fragment><IconButton onClick={() => onRemoveTermPolicy(key)}>{<Delete />}</IconButton></Fragment>}
      </Col>
      <Form.Group>
        <TextInput
          name={`coverageAmount`}
          id={`coverageAmountTerm${key}`}
          value={policy.coverageAmount}
          onChange={(e) => handleTermPolicyChange(key, 'coverageAmount', e.target.value)}
          label="Coverage Amount"
          inputRef={register}
          InputProps={{
            inputComponent: NumberFormatDollars,
          }}
        />
      </Form.Group>

      <Form.Group>
        <SelectInput
          name="termLength"
          id={`termLengthTerm${key}`}
          value={policy.termLength}
          onChange={(e) => handleTermPolicyChange(key, 'termLength', e.target.value)}
          label="Term Length"
          inputRef={register}
          selectOptions={termLengths}
        />
      </Form.Group>
    </Fragment>
  });

  const permForms = formState.permPolicies.map((policy, key) => {
    return <Fragment key={`permForm${key}`}>
      <Col className="title-control d-flex p-0 align-items-center">
        <span className={classes.formSectionTitle}>Permanent Life Insurance {key > 0 && <Fragment>{key + 1}</Fragment>}  (Optional)</span>
        {key > 0 &&
          <span>
            <IconButton onClick={() => onRemovePermPolicy(key)}>{<Delete />}</IconButton>
          </span>
        }
      </Col>
      <Form.Group>
        <TextInput
          name={`coverageAmount`}
          id={`coverageAmount${key}`}
          value={policy.coverageAmount}
          onChange={(e) => handlePermPolicyChange(key, 'coverageAmount', e.target.value)}
          label="Coverage Amount"
          inputRef={register}
          InputProps={{
            inputComponent: NumberFormatDollars,
          }}
        />
      </Form.Group>
      <Form.Group>
        <SelectInput
          name="coverageType"
          id={`coverageType${key}`}
          value={policy.coverageType}
          onChange={(e) => handlePermPolicyChange(key, 'coverageType', e.target.value)}
          label="Coverage Type"
          inputRef={register}
          selectOptions={coverageTypes}
        />
      </Form.Group>
    </Fragment>
  });

  return (

    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>

      <HeaderStepper
        title="Let's Start Your Policy Review"
        stepName='Existing Insurance'
        step={2}
        totalSteps={3}
        hideContact={true}
      >
        <Container className={`${classes.stepContainer}`}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="justify-content-center">
              <Col lg={4} md={6}>

                <Row className="justify-content-center pt-2">
                  <div className={`icon-container ${classes.lightBackground}`}>
                    <SvgIcon component={ExistingInsuranceIcon} color="primary" fontSize="large" />
                  </div>
                </Row>
                <Row className="justify-content-center mainTitle">
                  <h4>Existing Insurance Policy Details</h4>
                </Row>
                <Row className="row-content">
                  <Col>
                    <Form.Group>
                      <MultiSelectInput
                        selectOptions={[
                          { label: "Term Life Insurance", value: "Term Life Insurance" },
                          { label: "Permanent Life Insurance", value: "Permanent Life Insurance" },
                        ]}
                        name="currentInsuranceTypes"
                        id="currentInsuranceTypes"
                        value={formState.currentInsuranceTypes}
                        onChange={(e) => handleCurrentInsuranceTypesChange('currentInsuranceTypes', e.target.value)}
                        label="Current Insurance (Select all that apply)"
                        errors={errors && errors.currentInsuranceTypes}
                        inputRef={register}
                        renderLabelValue={true}
                      />
                    </Form.Group>

                    {formState.currentInsuranceTypes.includes('Term Life Insurance') &&
                      <div>
                        {termForms}

                        <Box textAlign="center">
                          <Button variant="text" onClick={onAddTermPolicy} className={`${classes.addButton} mb-3`}>Add another term policy</Button>
                        </Box>

                      </div>
                    }

                    {formState.currentInsuranceTypes.includes('Permanent Life Insurance') &&
                      <div>
                        {permForms}

                        <Box textAlign="center">
                          <Button variant="text" onClick={onAddPermPolicy} className={`${classes.addButton} mb-3`}>Add another permanent policy</Button>
                        </Box>

                      </div>
                    }

                  </Col>

                </Row>
              </Col>
            </Row>
            <Row className="nav-control">
              <Col>
                <BackNext onBack={() => props.history.push('/policy-review')} continueLabel={{ text: "Next" }} />
              </Col>
            </Row>
          </Form>
        </Container>
      </HeaderStepper>
    </ThemeProvider>
  )

}

ExistingInsurance.ValidationSchema = () => {
  return Yup.object().shape({
    currentInsuranceTypes: Yup.array()
      .min(1, 'Select at least one insurance type.')
      .required('Select at least one insurance type.')
  })
};

ExistingInsurance.DefaultFormState = (prefill = false, formState) => {
  return {
    currentInsuranceTypes: (formState && formState.currentInsuranceTypes) || [],
    termPolicies: (formState && formState.termPolicies) || [],
    permPolicies: (formState && formState.permPolicies) || [],
  };
};



// WEBPACK FOOTER //
// ./src/components/PolicyReview/ExistingInsurance.js